<script setup lang="ts">
import { PrimaryButton } from '@/components/Buttons'
import { StackLayout } from '@/components/Layouts'
import FullScreenLoader from '@/components/LoadingSpinner/FullScreenLoader.vue'
import { BaseDialog } from '@/components/Modals/Dialog'
import type { DeviceModel } from '@/types/models/DeviceModel'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import deviceApi from '../../../../../services/device.service'
import DeviceUpdateVersionStepOne from './DeviceUpdateVersionDialog/DeviceUpdateVersionStepOne.vue'
import DeviceUpdateVersionStepTwo from './DeviceUpdateVersionDialog/DeviceUpdateVersionStepTwo.vue'

type Props = {
    device: Partial<DeviceModel>
}
const props = defineProps<Props>()
type Emits = {
    (e: 'refresh'): void
}
const emit = defineEmits<Emits>()

const { t } = useI18n()

const open = ref(false)
const isLoading = ref(false)

const selectedVersionId = ref<number>()

const confirmVersionUpdate = () => {
    if ((!props.device?.device_id && !props.device?.id) || !selectedVersionId.value) return
    deviceApi
        .updateDeviceVersion(props.device.device_id ?? props.device.id!, selectedVersionId.value)
        .then(() => {
            emit('refresh')
            open.value = false
        })
}

const currentStep = ref(1)

const handleCloseButton = () => {
    if (currentStep.value === 1) open.value = false
    else if (currentStep.value === 2) currentStep.value = 1
}
const handleConfirmButton = () => {
    if (currentStep.value === 1) currentStep.value = 2
    else if (currentStep.value === 2) confirmVersionUpdate()
}

const cancelButtonLabel = computed(() =>
    currentStep.value === 1
        ? t('device.updateVersion.cancelStep')
        : t('device.updateVersion.previousStep')
)
const confirmButtonLabel = computed(() =>
    currentStep.value === 1
        ? t('device.updateVersion.nextStep')
        : t('device.updateVersion.updateStep')
)

const handleBackdropClick = () => {
    currentStep.value = 1
    open.value = false
}
</script>

<template>
    <FullScreenLoader :isLoading="isLoading" />
    <PrimaryButton
        :label="$t('device.updateVersion.buttonTitle')"
        @click="open = true"
        v-bind="$attrs" />
    <BaseDialog
        :title="$t('device.updateVersion.updateTitle')"
        :open="open"
        @backdropClick="handleBackdropClick"
        @close="handleCloseButton"
        @confirm="handleConfirmButton"
        :disabled="currentStep === 2 && !selectedVersionId"
        :closeLabel="cancelButtonLabel"
        :confirmLabel="confirmButtonLabel"
        class="dialog-with-dropdown">
        <template #content>
            <StackLayout class="full-width">
                <DeviceUpdateVersionStepOne v-if="currentStep === 1" />
                <DeviceUpdateVersionStepTwo
                    v-if="currentStep === 2"
                    :device="device"
                    :versionId="selectedVersionId"
                    @change="selectedVersionId = $event" />
            </StackLayout>
        </template>
    </BaseDialog>
</template>
